/**
 * Recording attendance from the volunteer's side.
 *
 * The activity screen already has a form for this, and it is the right one when
 * staff are working through a session's roster. This is the other direction, and
 * it is the one that was missing: somebody is standing in front of you, or on the
 * phone, and what you know is *who* and *when* — not which of eighty-six sessions
 * the hours belong to.
 *
 * So the form asks for the person (already known — it is their profile) and the
 * date, and answers the third question itself. `useRecentVolunteerSessions` returns
 * the handful of sessions this volunteer actually turns up to, most recently
 * attended first, and the top one is pre-selected. In the common case — a regular
 * volunteer at their regular session — staff change the duration and save.
 *
 * **Pre-selected, not decided.** The picker is a real picker and every suggestion
 * is visible; the ordering is a shortcut, not a guess being made on the user's
 * behalf. Each option says why it is being offered — the day they were last
 * recorded there, or that they are merely rostered — so a wrong pre-selection is
 * visible before it is saved rather than after.
 *
 * **It records; it does not decide.** Like the activity-side form, hours land as
 * PENDING and are settled in the attendance queue. There is one place to approve.
 *
 * The Plan D3 and D4 confirmations are deliberately *not* reimplemented here. If
 * the API asks a question this form cannot answer — recording somebody who is not
 * assigned, or a clash with hours already recorded — it says so and sends staff
 * to the activity screen, which has the full flow. Two half-implementations of a
 * confirmation dialogue is how they drift apart.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { isGroupCategory } from '@parinaam-vms/shared';
import type { Volunteer } from '@parinaam-vms/shared';
import {
  Button,
  Card,
  DurationInput,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectInput,
  TextInput,
} from '../../components/common';
import { useCreateAttendance } from '../../hooks/useAttendance';
import { useRecentVolunteerSessions } from '../../hooks/useSessions';
import type { RecentSession } from '../../services/api/sessions';
import { ApiRequestError } from '../../services/api/client';
import { formatDate } from '../../utils/format';
import styles from './RecordAttendancePanel.module.css';

export interface RecordAttendancePanelProps {
  volunteer: Volunteer;
}

/** Today, as the API wants it. Also the latest date the form will accept. */
function today(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Why this session is being suggested.
 *
 * "Last recorded 12 August" is the useful half — it is what a staff member
 * recognises. A session they are only rostered for says so instead, because
 * silently listing it beside places they genuinely attend would make the
 * ordering look arbitrary.
 */
function suggestionLabel(session: RecentSession): string {
  const where = `${session.activity_name} · ${session.session_name}`;
  if (session.last_attended) return `${where} — last recorded ${formatDate(session.last_attended)}`;
  return `${where} — assigned, not yet recorded`;
}

export function RecordAttendancePanel({ volunteer }: RecordAttendancePanelProps) {
  const [open, setOpen] = useState(false);

  return (
    <Card
      title="Record attendance"
      action={
        !open && (
          <Button variant="primary" onClick={() => setOpen(true)}>
            Record attendance
          </Button>
        )
      }
    >
      {open ? (
        <RecordForm volunteer={volunteer} onClose={() => setOpen(false)} />
      ) : (
        <p className={styles.intro}>
          Enter hours for {volunteer.first_name} without going to the activity first. Pick the day
          and the form will offer the sessions they have been attending.
        </p>
      )}
    </Card>
  );
}

function RecordForm({ volunteer, onClose }: { volunteer: Volunteer; onClose: () => void }) {
  const create = useCreateAttendance();
  const suggestions = useRecentVolunteerSessions(volunteer.volunteer_id);

  const [date, setDate] = useState(today());
  const [sessionId, setSessionId] = useState('');
  const [minutes, setMinutes] = useState(0);
  const [headcount, setHeadcount] = useState(1);
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const sessions = suggestions.data ?? [];

  // The auto-population. The first suggestion is where this volunteer was most
  // recently recorded, which is the answer often enough to be worth defaulting
  // to — and `sessionId` stays empty until staff choose, so their choice always
  // wins over the default rather than fighting a re-render for it.
  const chosenSession = sessionId || sessions[0]?.session_id || '';

  const isGroup = isGroupCategory(volunteer.category as Parameters<typeof isGroupCategory>[0]);

  if (suggestions.isPending) return <LoadingState label="Finding recent sessions…" />;
  if (suggestions.isError) {
    return <ErrorState error={suggestions.error} onRetry={() => void suggestions.refetch()} />;
  }

  if (sessions.length === 0) {
    return (
      <EmptyState
        title="Nothing to record against yet"
        message={`${volunteer.first_name} has no attendance history and no current assignment, so there is nothing to suggest. Assign them to an activity first, then record the hours there.`}
      />
    );
  }

  const submit = () => {
    const problems: Record<string, string> = {};
    if (!date) problems.attendance_date = 'When did they attend?';
    if (date > today()) {
      problems.attendance_date = 'Attendance cannot be recorded for a date in the future.';
    }
    if (!chosenSession) problems.session_id = 'Choose which session these hours were given to.';
    if (Object.keys(problems).length > 0) {
      setErrors(problems);
      return;
    }

    create.mutate(
      {
        volunteer_id: volunteer.volunteer_id,
        session_id: chosenSession,
        attendance_date: date,
        duration_minutes: minutes,
        ...(isGroup ? { participant_count: headcount } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      },
      { onSuccess: onClose }
    );
  };

  // A refusal this form cannot answer. Both of these have a full flow on the
  // activity screen — an "are you sure" with the detail behind it — and a second,
  // thinner version of that here would be the one that goes out of date.
  const needsTheOtherForm =
    create.error instanceof ApiRequestError &&
    (create.error.code === 'ASSIGNMENT_REQUIRED' || create.error.code === 'OVERLAP_DETECTED');

  const selected = sessions.find((session) => session.session_id === chosenSession);

  return (
    <div className={styles.form}>
      <div className={styles.fields}>
        <TextInput
          label="Date"
          type="date"
          required
          max={today()}
          value={date}
          error={errors.attendance_date}
          onChange={(event) => {
            setDate(event.target.value);
            setErrors((current) => ({ ...current, attendance_date: '' }));
          }}
        />

        <SelectInput
          label="Session"
          required
          options={sessions.map((session) => ({
            value: session.session_id,
            label: suggestionLabel(session),
          }))}
          value={chosenSession}
          error={errors.session_id}
          onChange={(event) => {
            setSessionId(event.target.value);
            setErrors((current) => ({ ...current, session_id: '' }));
          }}
        />

        <DurationInput
          label="How long"
          required
          value={minutes}
          error={errors.duration_minutes}
          onChange={(value) => {
            setMinutes(value);
            setErrors((current) => ({ ...current, duration_minutes: '' }));
          }}
        />

        {isGroup && (
          <TextInput
            label="How many people came"
            type="number"
            min={1}
            required
            value={String(headcount)}
            onChange={(event) => setHeadcount(Number(event.target.value))}
          />
        )}

        <TextInput label="Notes" value={notes} onChange={(event) => setNotes(event.target.value)} />
      </div>

      {selected && (
        <p className={styles.context}>
          {selected.program_name} · {selected.activity_name} ·{' '}
          <Link to={`/activities/${selected.activity_id}`}>open the activity</Link>
        </p>
      )}

      {create.isError && (
        <p className={styles.error} role="alert">
          {(create.error as Error).message}
          {needsTheOtherForm && selected && (
            <>
              {' '}
              <Link to={`/activities/${selected.activity_id}`}>
                Record it on the activity screen
              </Link>
              , which can confirm this and record it anyway.
            </>
          )}
        </p>
      )}

      <div className={styles.actions}>
        <Button variant="primary" loading={create.isPending} onClick={submit}>
          Record attendance
        </Button>
        <Button onClick={onClose} disabled={create.isPending}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
